import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { DynamicLayoutComponentApi } from '@common-modules/dynamic-layout/dynamic-layout/dynamic-layout-component-api';
import { DynamicLayoutComponent } from '@common-modules/dynamic-layout/dynamic-layout/dynamic-layout.component';
import { DynamicLayoutBuilderService } from '@common-modules/dynamic-layout/layouts/dynamic-layout-builder-service';
import { SimpleDynamicLayoutSettingsItem } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings-item';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { DynamicLayoutStructure } from '@common-modules/dynamic-layout/models/dynamic-layout-structure';
import { LayoutNodeTypes } from '@common-modules/dynamic-layout/models/layout-node-types';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { LogService } from '@common-modules/shared/wlm-log/log.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MasterOperatorSingleSettings } from '../master-operator-single/master-operator-single-settings';
import { MasterOperatorType } from '../models/master-operator-type';
import { MasterOperatorGroupApi } from './master-operator-group-api';
import { MasterOperatorGroupSettings } from './master-operator-group-settings';

const COMPONENT_SELECTOR = 'tc-master-operator-group';

@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './master-operator-group.component.html',
    styleUrls: ['./master-operator-group.component.scss'],
    standalone: false
})
export class MasterOperatorGroupComponent implements OnInit, MasterOperatorGroupApi {
  @ViewChild(DynamicLayoutComponent) dynamicLayoutComponent: DynamicLayoutComponentApi;

  private _settings: MasterOperatorGroupSettings;
  get settings(): MasterOperatorGroupSettings {
    return this._settings;
  }
  @Input() set settings(value: MasterOperatorGroupSettings) {
    this._settings = value;

    if (this.settings) {
      if (this.settings.innerLayout) {
        this.dynamicLayoutSettings = this.settings.innerLayout;
      } else {
        this.buildDynamicLayoutSettings().subscribe((settings) => {
          this.dynamicLayoutSettings = settings;
          this.dynamicLayoutSettings.debugName = 'GroupLayout';
        });
      }
    }
  }

  private _addOperator$: Observable<MasterOperatorSingleSettings>;
  public get addOperator$(): Observable<MasterOperatorSingleSettings> {
    return this._addOperator$;
  }
  @Input() public set addOperator$(value: Observable<MasterOperatorSingleSettings>) {
    this._addOperator$ = value;
    if (value) {
      value.subscribe((settings) => {
        this.addOperatorWidget(settings);
      });
    }
  }

  dynamicLayoutSettings: DynamicLayoutSettings;
  readonly T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;

  constructor(
    private _dynamicLayoutBuilderService: DynamicLayoutBuilderService,
    private _log: LogService
  ) {}

  ngOnInit(): void {}

  /**
   * Build the dynamic layout with a default master widget, and include an operator if configured.
   * Also generate a side to side layout if there are two widgets.
   */
  buildDynamicLayoutSettings(): Observable<DynamicLayoutSettings> {
    const simpleSettings = globalUtilsHelper.clone(this.settings.layout, true);

    const masterWidget = this.buildMasterWidget();
    const defaultWidgets = [masterWidget];
    let defaultOperator;

    // if (this.settings.addDefaultOperator) {
    //   defaultOperator = this.buildOperatorWidget();
    //   defaultWidgets.push(defaultOperator);
    // }

    simpleSettings.items = defaultWidgets;

    return this._dynamicLayoutBuilderService.build(simpleSettings).pipe(
      map((dynamicLayoutSettings) => {
        dynamicLayoutSettings.showCloseWidgetIcon = true;
        dynamicLayoutSettings.defaultStructure = this.buildDefaultStructure(
          masterWidget,
          defaultOperator
        );

        dynamicLayoutSettings.delegatePersistency = {
          currentId: this.settings.groupId,
          delegateToId: this.settings.parentId,
        };

        return dynamicLayoutSettings;
      })
    );
  }

  /**
   * Add a newly generated operator widget to the layout.
   */
  addOperatorWidget(settings: MasterOperatorSingleSettings): void {
    if (!this.settings || !this.dynamicLayoutComponent) {
      this._log.error({ msg: 'Could not add widget, the component is not ready yet.' });
      return;
    }

    const simpleOperatorWidget = this.buildOperatorWidget(settings);

    this._dynamicLayoutBuilderService
      .buildExternalItem(this.settings.layout, simpleOperatorWidget)
      .subscribe((widgetSettings) => {
        this.dynamicLayoutComponent.addWidget(widgetSettings);
      });
  }

  private buildDefaultStructure(
    masterWidget: SimpleDynamicLayoutSettingsItem,
    defaultOperator?: SimpleDynamicLayoutSettingsItem
  ): DynamicLayoutStructure[] {
    const structure: DynamicLayoutStructure[] = [
      {
        type: LayoutNodeTypes.Row,
        content: [
          {
            type: LayoutNodeTypes.Stack,
            content: [
              {
                type: LayoutNodeTypes.Component,
                widgetInstanceKey: masterWidget.id,
              },
            ],
          },
        ],
      },
    ];

    if (defaultOperator) {
      (structure[0] as any).content.push({
        type: LayoutNodeTypes.Stack,
        content: [
          {
            type: LayoutNodeTypes.Component,
            widgetInstanceKey: defaultOperator.id,
          },
        ],
      });
    }

    return structure;
  }

  /**
   * Build a master widget and link it to the groupId of the current group.
   */
  private buildMasterWidget(): SimpleDynamicLayoutSettingsItem {
    const widgetId = globalUtilsHelper.generateGuid();
    const widget: SimpleDynamicLayoutSettingsItem = {
      widgetName: 'MasterOperatorSingleWidgetComponent',
      id: widgetId,
      titleKey: null,
      // titleKey: `${TranslateScopes.MasterOperator}.entities.master`,
      params: {
        type: MasterOperatorType.Master,
        singleId: widgetId,
        groupId: this.settings.groupId,
        layout: { ...this.settings.layout },
        isSingleSettings: true,
        // Masters are preconfigured at the creation of the workspace.
        connect: this.settings.workspace.masterConnectItemsSettings,
      } as MasterOperatorSingleSettings,
    };

    return widget;
  }

  /**
   * Build an operator widget and link it to the groupId of the current group.
   */
  private buildOperatorWidget(
    settings: MasterOperatorSingleSettings
  ): SimpleDynamicLayoutSettingsItem {
    const widgetId = globalUtilsHelper.generateGuid();
    const widget: SimpleDynamicLayoutSettingsItem = {
      widgetName: 'MasterOperatorSingleWidgetComponent',
      id: widgetId,
      titleKey: `${TranslateScopes.MasterOperator}.entities.operator`,
      params: {
        type: MasterOperatorType.Operator,
        singleId: widgetId,
        connect: settings.connect,
        groupId: settings.groupId,
        layout: { ...this.settings.layout },
        isSingleSettings: true,
      } as MasterOperatorSingleSettings,
    };

    return widget;
  }

  // TODO: use in next iteration.
  private buildLayoutKey(widgetId: string): string {
    return [this.settings.layout.layoutKey, widgetId].join('#');
  }
}
